import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { CarrierFilters, Carriers } from '../models';
import * as carriersService from '../services/carriers.service';

export const useCarriers = (defaultFilters: Partial<CarrierFilters & { searchParams: { ids?: string[] } }> = {}) => {
  const { searchParams, ...otherDefaultFilters } = useMemo(() => defaultFilters, [defaultFilters]);
  const [filters, setFilters] = useState<Partial<CarrierFilters>>({});

  const mergedFilters = { ...otherDefaultFilters, ...filters };

  const hasSearchValue = Object.values(mergedFilters.search ?? {}).some(Boolean);
  const isEnabled = 'customerKey' in defaultFilters ? Boolean(mergedFilters.customerKey) : hasSearchValue;
  const updateFilters = useCallback((newFilters: Partial<CarrierFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const { data, isLoading, isError, isFetching } = useQuery(
    carriersService.carriersQueryOptions(mergedFilters, { enabled: isEnabled }),
  );

  const missingIds = useMemo(() => {
    if (!searchParams?.ids?.length) {
      return [];
    }
    if (!data?.map) {
      return searchParams.ids;
    }
    return searchParams.ids.filter((id) => !data.map[id]);
  }, [searchParams?.ids, data?.map]);

  const {
    data: preloadData,
    isLoading: isPreloadLoading,
    isError: isPreloadError,
    isFetching: isPreloadFetching,
  } = useQuery(
    carriersService.carriersQueryOptions({ search: { id: missingIds.join(',') } }, { enabled: missingIds.length > 0 }),
  );
  const mergedMap: Carriers['map'] = { ...(data?.map ?? {}), ...(preloadData?.map ?? {}) };

  return {
    data: {
      list: Object.values(mergedMap),
      map: mergedMap,
      total: data?.total ?? 0,
    },
    isError: isError || isPreloadError,
    isFetching: isFetching || isPreloadFetching,
    isLoading: isLoading || isPreloadLoading,
    updateFilters,
  };
};
